import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ApiService from '../../services/ApiService';
import MangaApiService from '../../services/MangaApiService';
import AnimeCardComponent from '../../components/animecard/AnimeCardComponent';
import MangaCardComponent from '../../components/mangacard/MangaCardComponent';
import { unwrapList } from '../manga/mangaStudioUtils';
import { MANGA_STATUSES } from './ProfileCollectionsCard';

// The entities endpoint accepts at most 10 ids per call.
const PAGE = 10;

/**
 * Someone's collections inside the profile: anime collections (paged by ids)
 * and manga lists by status, switched with chips. No navigation away from the profile.
 */
const ProfileCollectionsPanel = ({ userId, kind, onKindChange, manga }) => {
    const current = kind === 'manga' ? 'manga' : 'anime';
    const mangaLists = manga?.lists || {};

    // Anime: collections come with their anime ids; cards are fetched PAGE at a time.
    const [collections, setCollections] = useState(null);
    const [selected, setSelected] = useState(null);
    const [animeList, setAnimeList] = useState([]);
    const [loadedCount, setLoadedCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const request = useRef(0);

    useEffect(() => {
        let active = true;
        setCollections(null);
        setSelected(null);
        if (!userId) return undefined;
        ApiService.getCollections(userId)
            .then((response) => {
                if (!active || response?.type !== 'success') return;
                const list = Array.isArray(response.data) ? response.data : [];
                setCollections(list);
                setSelected(list.find((entry) => entry.animeIds?.length) || list[0] || null);
            })
            .catch(() => active && setCollections([]));
        return () => {
            active = false;
        };
    }, [userId]);

    const ids = selected ? Array.from(new Set(selected.animeIds || [])) : [];

    const loadMore = async (from) => {
        const slice = ids.slice(from, from + PAGE);
        if (slice.length === 0) return;
        const ticket = ++request.current;
        setLoading(true);
        try {
            const response = await ApiService.getAnimeByIds(slice);
            if (ticket !== request.current) return;
            if (response?.type === 'success') {
                setAnimeList((prev) => (from === 0 ? response.data : [...prev, ...response.data]));
                setLoadedCount(from + slice.length);
            }
        } finally {
            if (ticket === request.current) setLoading(false);
        }
    };

    useEffect(() => {
        setAnimeList([]);
        setLoadedCount(0);
        if (selected) loadMore(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    // Manga: one request per status.
    const [mangaStatus, setMangaStatus] = useState(null);
    const [mangaItems, setMangaItems] = useState([]);
    const [mangaLoading, setMangaLoading] = useState(false);

    // Start on the first non-empty list, so the manga side never opens on an empty tab.
    useEffect(() => {
        const first = MANGA_STATUSES.find((status) => Number(mangaLists[status.key]) > 0);
        setMangaStatus((value) => value || (first ? first.key : MANGA_STATUSES[0].key));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manga]);

    useEffect(() => {
        if (!mangaStatus || current !== 'manga') return undefined;
        let active = true;
        setMangaLoading(true);
        MangaApiService.getUserMangaListsOf(userId, mangaStatus)
            .then((response) => active && setMangaItems(unwrapList(response).map((entry) => entry.manga || entry).filter(Boolean)))
            .catch(() => active && setMangaItems([]))
            .finally(() => active && setMangaLoading(false));
        return () => {
            active = false;
        };
    }, [userId, mangaStatus, current]);

    const empty = (text) => (
        <div className="profile-empty-state"><p>{text}</p></div>
    );

    return (
        <div className="profile-collections-panel">
            <div className="profile-collections-panel__bar">
                <div className="profile-seg" role="tablist" aria-label="Тип коллекции">
                    <button type="button" role="tab" aria-selected={current === 'anime'} onClick={() => onKindChange('anime')}>Аниме</button>
                    <button type="button" role="tab" aria-selected={current === 'manga'} onClick={() => onKindChange('manga')}>Манга</button>
                </div>

                {current === 'anime' && collections?.length ? (
                    <div className="ui-chips" role="tablist" aria-label="Коллекция">
                        {collections.map((collection) => (
                            <button
                                key={collection.id}
                                type="button"
                                role="tab"
                                className="ui-chip"
                                aria-selected={selected === collection}
                                onClick={() => setSelected(collection)}
                            >
                                {collection.name}
                                <span className="profile-chip-count">{new Set(collection.animeIds || []).size}</span>
                            </button>
                        ))}
                    </div>
                ) : null}

                {current === 'manga' ? (
                    <div className="ui-chips" role="tablist" aria-label="Список манги">
                        {MANGA_STATUSES.map((status) => (
                            <button
                                key={status.key}
                                type="button"
                                role="tab"
                                className="ui-chip"
                                aria-selected={mangaStatus === status.key}
                                onClick={() => setMangaStatus(status.key)}
                            >
                                {status.label}
                                <span className="profile-chip-count">{Number(mangaLists[status.key]) || 0}</span>
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>

            {current === 'anime' ? (
                <>
                    {animeList.length > 0 ? (
                        <div className="anime-container profile-collections-panel__grid">
                            {animeList.map((anime) => (
                                <AnimeCardComponent key={anime.id} anime={anime} isInteractive={true} />
                            ))}
                        </div>
                    ) : null}
                    {!loading && animeList.length === 0 ? (
                        collections === null ? null : empty(collections.length ? 'В этой коллекции пока пусто.' : 'Коллекций пока нет.')
                    ) : null}
                    {loading ? <p className="ui-status">Загружаем…</p> : null}
                    {!loading && loadedCount < ids.length ? (
                        <button type="button" className="profile-card__more" onClick={() => loadMore(loadedCount)}>
                            Показать ещё
                        </button>
                    ) : null}
                </>
            ) : (
                <>
                    {mangaLoading ? <p className="ui-status">Загружаем…</p> : mangaItems.length > 0 ? (
                        <div className="anime-container profile-collections-panel__grid">
                            {mangaItems.map((item) => (
                                <MangaCardComponent key={item.id || item.slug} manga={item} isInteractive />
                            ))}
                        </div>
                    ) : empty('В этом списке пока пусто.')}
                </>
            )}
        </div>
    );
};

ProfileCollectionsPanel.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    kind: PropTypes.string,
    onKindChange: PropTypes.func.isRequired,
    manga: PropTypes.object,
};

ProfileCollectionsPanel.defaultProps = {
    userId: null,
    kind: 'anime',
    manga: null,
};

export default ProfileCollectionsPanel;
